import { z } from "zod";

/** Longest note text, sized to hold the largest e-mail thread migrated from Clientar. */
export const NOTE_TEXT_MAX = 12_000;

export const NoteMentionSchema = z.object({
  userId: z.string().min(1).describe("Cognito sub of the mentioned user"),
  start: z.number().int().nonnegative().describe("Inclusive UTF-16 offset of the mention"),
  end: z.number().int().positive().describe("Exclusive UTF-16 offset of the mention"),
});
export type NoteMention = z.infer<typeof NoteMentionSchema>;

const validateMentions = (
  value: { text: string; mentions?: NoteMention[] },
  context: z.core.$RefinementCtx,
) => {
  let previousEnd = 0;

  for (const [index, mention] of (value.mentions ?? []).entries()) {
    const validRange = mention.start < mention.end && mention.end <= value.text.length;
    const isOrdered = index === 0 || mention.start >= previousEnd;
    const startsWithAt = validRange && value.text[mention.start] === "@";

    if (!validRange || !isOrdered || !startsWithAt) {
      context.addIssue({
        code: "custom",
        path: ["mentions", index],
        message: "Mention ranges must be ordered, non-overlapping, in bounds, and start with @.",
      });
    }

    previousEnd = Math.max(previousEnd, mention.end);
  }
};

export const NoteSchema = z
  .object({
    id: z.uuid(),
    text: z.string().min(1).max(NOTE_TEXT_MAX),
    mentions: z.array(NoteMentionSchema).max(20).default([]),
    userId: z
      .string()
      .min(1)
      .describe("Cognito sub of the author; display name is resolved on read"),
    createdAt: z.iso.datetime(),
    editedAt: z.iso.datetime().nullish().describe("Set the first time the note's text is changed"),
    deletedAt: z.iso
      .datetime()
      .nullish()
      .describe("Set when the note is soft-deleted; hidden from anyone without manageOtherNotes"),
  })
  .superRefine(validateMentions);

export type Note = z.infer<typeof NoteSchema>;

export const AddNoteSchema = z.object({
  text: z.string().trim().min(1).max(NOTE_TEXT_MAX),
});

export const AddCaseNoteSchema = z
  .object({
    text: z
      .string()
      .min(1)
      .max(NOTE_TEXT_MAX)
      .refine((text) => text === text.trim()),
    mentions: z.array(NoteMentionSchema).max(20).default([]),
  })
  .superRefine(validateMentions);
export type AddCaseNoteInput = z.input<typeof AddCaseNoteSchema>;
